import React from "react";
import { useAppSelector } from "../../../hooks/hooks";

// redux getters
import {
  selectPropertyReport,
  selectPropertyReportStatus,
} from "../../../features/reports/reportsSlice";

// utils
import {
  generateList,
  formatNumberCommas,
  parseDollarStringToNumber,
} from "../../../utils";

// components
import {
  Container,
  Layout,
  Padding,
  Text,
  CollapsableItemList,
  ContainerLineItem,
} from "@kidslivesafe/sc-react";

import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";

const ValueTaxes: React.FC<{ containerId: string; printReady?: boolean }> = ({
  containerId,
  printReady,
}) => {
  const pulledReport = useAppSelector(selectPropertyReport);
  const propertyReportStatus = useAppSelector(selectPropertyReportStatus);

  const loading = propertyReportStatus === "loading";

  return (
    <Padding
      left="md"
      right="md"
      breakpointStyles={{
        desktop: {
          space: "none",
        },
      }}
    >
      <Container
        containerTitle={`Assessed Value & Taxes ${
          loading
            ? ""
            : `(${
                pulledReport?.propertyAssessments
                  ? pulledReport.propertyAssessments.length
                  : 0
              })`
        }`}
        containerId={containerId}
        tooltipContent="Assessed value is the County Assessor's estimate of a property's worth determined by factors like location, size, condition, and comparable properties nearby. It is used to determine property tax rates, and may not reflect the current market value of the property."
      >
        {loading ? (
          <LoadingContainerLineItem
            lineItemHeader
            lineItemSubHeader
            numberToLoad={5}
            children
          />
        ) : pulledReport?.propertyAssessments &&
          pulledReport.propertyAssessments[0] ? (
          <CollapsableItemList numberToShow={5} isOpen={printReady}>
            {pulledReport.propertyAssessments.map((assessment, index) => (
              <ContainerLineItem
                lineItemHeader={`${
                  assessment.taxAmount
                    ? formatNumberCommas(assessment.taxAmount, true)
                    : ""
                } Property Tax`}
                lineItemSubHeader={generateList(
                  [
                    {
                      value: assessment.taxYear ? assessment.taxYear : null,
                      label: "",
                    },
                    {
                      value: assessment.assessedTotalValue
                        ? assessment.assessedTotalValue +
                          " Total Assessed Value"
                        : null,
                      label: "",
                    },
                  ],
                  " | ",
                )}
                isOpen={printReady || index === 0 ? true : false}
                key={index}
              >
                <Padding space="md" top="none">
                  <Container altColor="grey">
                    <Padding space="md">
                      <Layout
                        fillHorizontal
                        type="grid"
                        gridProps={{
                          templateColumns: "3-column",
                          alignItems: "center",
                        }}
                        breakpointStyles={{
                          mobile: {
                            type: "flex",
                            flexProps: {
                              direction: "column",
                              gap: "md",
                              alignItems: "flex-start",
                            },
                          },
                        }}
                      >
                        <Layout type="flex" flexProps={{ direction: "column" }}>
                          <Text size="sm">Land Value</Text>
                          {assessment.assessedLandValue ? (
                            <Text size="sm" fontWeight="bold">
                              {assessment.assessedLandValue}
                              {assessment.assessedTotalValue && (
                                <Text size="sm" tag="span" fontWeight="normal">
                                  {" ("}
                                  {Math.round(
                                    (parseDollarStringToNumber(
                                      assessment.assessedLandValue,
                                    ) /
                                      parseDollarStringToNumber(
                                        assessment.assessedTotalValue,
                                      )) *
                                      1000,
                                  ) / 10}
                                  %{")"}
                                </Text>
                              )}
                            </Text>
                          ) : (
                            <Text size="sm" fontWeight="bold">
                              N/A
                            </Text>
                          )}
                        </Layout>
                        <Layout type="flex" flexProps={{ direction: "column" }}>
                          <Text size="sm">Improvements</Text>
                          {assessment.assessedImprovementValue ? (
                            <Text size="sm" fontWeight="bold">
                              {assessment.assessedImprovementValue}
                              {assessment.assessedTotalValue && (
                                <Text size="sm" tag="span" fontWeight="normal">
                                  {" ("}
                                  {Math.round(
                                    (parseDollarStringToNumber(
                                      assessment.assessedImprovementValue,
                                    ) /
                                      parseDollarStringToNumber(
                                        assessment.assessedTotalValue,
                                      )) *
                                      1000,
                                  ) / 10}
                                  %{")"}
                                </Text>
                              )}
                            </Text>
                          ) : (
                            <Text size="sm" fontWeight="bold">
                              N/A
                            </Text>
                          )}
                        </Layout>
                        <Layout type="flex" flexProps={{ direction: "column" }}>
                          <Text size="sm">Total Value</Text>
                          {assessment.assessedTotalValue ? (
                            <Text size="sm" fontWeight="bold">
                              {assessment.assessedTotalValue}
                            </Text>
                          ) : (
                            <Text size="sm" fontWeight="bold">
                              N/A
                            </Text>
                          )}
                        </Layout>
                      </Layout>
                    </Padding>
                  </Container>
                </Padding>
              </ContainerLineItem>
            ))}
          </CollapsableItemList>
        ) : (
          <Padding space="md">
            <Text size="sm" tag="b">
              No results found for Assessed Value and Taxes
            </Text>
          </Padding>
        )}
      </Container>
    </Padding>
  );
};

export default ValueTaxes;
